import React, { useContext } from 'react'
import { ChevronRightIcon, PlusCircleIcon, MinusCircleIcon, PencilSquareIcon, ArrowPathIcon } from '@heroicons/react/20/solid'
import { CustomSendIMailIcon } from './CustomSendIMailIcon'
import { DealerContext } from '@/context/DealerContext'

interface ButtonProps {
  style?: 'default' | 'text'
  icon?: 'default' | 'add' | 'remove' | 'edit' | 'mail' | 'none'
  iconPosition?: 'left' | 'right'
  children?: React.ReactNode
  type?: 'button' | 'submit' | 'reset'
  name?: string
  value?: string
  disabled?: boolean,
  onClick?: () => void
  className?: string
  loadingText?: string
  loading?: boolean
}

const Button: React.FC<ButtonProps> = ({
    icon = 'default',
    style = 'default',
    iconPosition = 'right',
    disabled = false,
    onClick, className = '',
    children,
    type = 'button',
    loadingText = 'loading',
    loading = false,
    name,
    value,
    ...props
  }) => {
  const dealerContext = useContext(DealerContext)
  const radius = dealerContext?.buttonShape || 'rounded-xl'
  const iconClass = `mb-0.5 size-5 transition duration-300 ${iconPosition === 'left' ? '-ml-0.5 group-hover:-translate-x-1 -scale-x-100' : '-mr-0.5 group-hover:translate-x-1'}`
  const renderIcon = () => {
    
    switch (icon) {
      case 'add':
        return <PlusCircleIcon aria-hidden='true' className={iconClass} />
      case 'remove':
        return <MinusCircleIcon aria-hidden='true' className={iconClass} />
      case 'edit':
        return <PencilSquareIcon aria-hidden='true' className={iconClass} />
      case 'mail':
        return <CustomSendIMailIcon className={`${iconClass} size-6`} />
      case 'none':
        return <></>
      default:
        return <ChevronRightIcon aria-hidden='true' className={iconClass} />
    }
  }

  const buttonClasses = style === 'text'
    ? 'text-[var(--primary-button-bg)] hover:bg-[var(--theme-bg)]'
    : 'bg-[var(--primary-button-bg)] text-[var(--primary-button-text)] hover:bg-[var(--primary-button-bg-hover)]'

  return (
    <button
        disabled={disabled}
        type={type}
        name={name}
        value={value}
        className={`
          appearance-none user-select-none whitespace-nowrap group leading-[1] inline-flex justify-center cursor-pointer items-center gap-x-2 ${radius} px-6 py-3.5 text-sm 
          font-[var(--font-weight-bold)] transition duration-300
          focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary-button-bg-hover)]
          disabled:opacity-60 disabled:pointer-events-none [&.disabled]:opacity-60 [&.disabled]:pointer-events-none
          ${buttonClasses} ${className}
          ${loading ? 'pointer-events-none text-[var(--theme-text-alt)]' : ''}
        `}
        onClick={onClick}
        {...props}
      >
        { loading && iconPosition === 'left' && <ArrowPathIcon aria-hidden='true' className={`animate-spin ${iconClass}`} /> }
        {iconPosition === 'left' && !loading && renderIcon()}
        { !loading && children }
        { loading && <>{loadingText}</> }
        {iconPosition === 'right' && !loading && renderIcon()}
        { loading && iconPosition === 'right' && <ArrowPathIcon aria-hidden='true' className={`animate-spin ${iconClass}`} /> }
    </button>
  )
}

export {Button}
